import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkle, ArrowRight, CheckCircle, Warning } from "@phosphor-icons/react";

/**
 * /reset-password?token=... — public route, used from the password-reset
 * email link. Validates token by POST'ing it with the new password.
 * Backend enforces token expiry, single-use, and password strength.
 */
export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();

    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) setErr("This reset link is missing its token. Please request a new one.");
    }, [token]);

    const pwStrength = useMemo(() => {
        if (!pw) return null;
        if (pw.length < 8) return { ok: false, label: "Too short — must be 8+ characters" };
        if (pw === pw2) return { ok: true, label: "Looks good" };
        if (pw2 && pw !== pw2) return { ok: false, label: "Passwords don't match" };
        return { ok: true, label: "Confirm to continue" };
    }, [pw, pw2]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        if (pw.length < 8) {
            setErr("Password must be at least 8 characters.");
            return;
        }
        if (pw !== pw2) {
            setErr("Passwords don't match.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            setDone(true);
            setTimeout(() => navigate("/auth", { replace: true }), 2500);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Something went wrong. Try requesting a new reset link.");
        }
        setBusy(false);
    };

    return (
        <div className="min-h-[80vh] grid place-items-center px-4 py-12">
            <div className="w-full max-w-md" data-testid="reset-password-page">
                <div className="flex items-center gap-2 mb-6 justify-center">
                    <div className="h-10 w-10 rounded-xl bg-[#6C63FF] grid place-items-center text-white">
                        <Sparkle size={20} weight="fill" />
                    </div>
                    <div className="font-display font-black text-xl tracking-tight">
                        <span className="text-[#6C63FF]">Ideas</span><span className="text-[#FF8A00]">Mine</span>
                    </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-7">
                    {done ? (
                        <div className="text-center" data-testid="reset-password-success">
                            <CheckCircle size={48} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
                            <h1 className="font-display font-black text-2xl mb-2 tracking-tight">Password updated</h1>
                            <p className="text-sm text-muted-foreground">
                                You'll be redirected to sign in shortly.
                            </p>
                        </div>
                    ) : (
                        <>
                            <h1 className="font-display font-black text-2xl mb-1 tracking-tight">Choose a new password</h1>
                            <p className="text-sm text-muted-foreground mb-5">
                                Pick something strong — at least 8 characters.
                            </p>
                            <form onSubmit={onSubmit} className="space-y-4">
                                <div>
                                    <Label htmlFor="new-pw" className="text-sm font-semibold">New password</Label>
                                    <Input
                                        id="new-pw"
                                        type="password"
                                        value={pw}
                                        onChange={(e) => setPw(e.target.value)}
                                        required
                                        autoFocus
                                        minLength={8}
                                        maxLength={128}
                                        autoComplete="new-password"
                                        className="mt-1"
                                        data-testid="reset-password-new"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="new-pw2" className="text-sm font-semibold">Confirm password</Label>
                                    <Input
                                        id="new-pw2"
                                        type="password"
                                        value={pw2}
                                        onChange={(e) => setPw2(e.target.value)}
                                        required
                                        minLength={8}
                                        maxLength={128}
                                        autoComplete="new-password"
                                        className="mt-1"
                                        data-testid="reset-password-confirm"
                                    />
                                </div>
                                {pwStrength && (
                                    <div className={`text-xs ${pwStrength.ok ? "text-emerald-600" : "text-amber-600"}`}>
                                        {pwStrength.label}
                                    </div>
                                )}
                                {err && (
                                    <div className="text-sm text-red-500 flex items-start gap-1.5" data-testid="reset-password-error">
                                        <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
                                        <span>{err}</span>
                                    </div>
                                )}
                                <Button
                                    type="submit"
                                    disabled={busy || !token || !pw || !pw2}
                                    className="w-full brand-button"
                                    data-testid="reset-password-submit"
                                >
                                    {busy ? "Updating…" : "Update password"} <ArrowRight size={16} className="ml-1" />
                                </Button>
                            </form>
                            <p className="text-xs text-center text-muted-foreground mt-4">
                                Don't need this?{" "}
                                <Link to="/auth" className="text-[#6C63FF] font-semibold hover:underline" data-testid="reset-password-back-link">
                                    Back to sign in
                                </Link>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
